import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/lib/useSeo";
import { hotelierRegister, hotelierMe } from "@/lib/api";

export default function HotelierRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    hotel_name: "",
    location: "",
    contact_name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Proponi il tuo hotel — SuiteCrowd",
    description:
      "Sei il titolare di un hotel? Proponi il tuo progetto di ristrutturazione a SuiteCrowd: crea l'account, carica foto, aggiorna l'avanzamento lavori e attiva la raccolta.",
    canonical: "https://suitecrowd.com/proponi",
  });

  useEffect(() => {
    hotelierMe().then(() => navigate("/hotelier/dashboard", { replace: true })).catch(() => {});
  }, [navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await hotelierRegister(form);
      toast.success("Account creato. Benvenuto a bordo.");
      navigate("/hotelier/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registrazione non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#121212]">
      <nav className="border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <Logo variant="dark" size="md" />
          <Link to="/" className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 hover:text-[#121212] transition-colors">
            Home
          </Link>
        </div>
      </nav>

      <section className="max-w-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="sc-overline mb-6">Portale Hotelier</div>
        <h1 className="font-display font-light text-4xl md:text-5xl mb-4" style={{ letterSpacing: "-0.03em" }}>
          Proponi il tuo hotel
        </h1>
        <p className="text-neutral-600 leading-relaxed mb-12 max-w-xl">
          Crea l'account per la tua struttura. Potrai caricare foto, descrivere il
          progetto e pubblicare gli aggiornamenti dei lavori. La pubblicazione è
          soggetta a validazione da parte del team SuiteCrowd.
        </p>

        <form data-testid="hotelier-register-form" onSubmit={submit} className="space-y-6 max-w-xl">
          <div>
            <label className="sc-overline block mb-2">Nome della struttura</label>
            <input required data-testid="reg-hotel-name" value={form.hotel_name} onChange={set("hotel_name")} className="sc-input-light" placeholder="es. Grand Hotel Olivier Prince" />
          </div>
          <div>
            <label className="sc-overline block mb-2">Località</label>
            <input required data-testid="reg-location" value={form.location} onChange={set("location")} className="sc-input-light" placeholder="es. Roseto degli Abruzzi, Italia" />
          </div>
          <div>
            <label className="sc-overline block mb-2">Nome referente</label>
            <input data-testid="reg-contact" value={form.contact_name} onChange={set("contact_name")} className="sc-input-light" placeholder="Nome e cognome" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="sc-overline block mb-2">Email</label>
              <input required type="email" data-testid="reg-email" value={form.email} onChange={set("email")} className="sc-input-light" placeholder="tu@hotel.com" />
            </div>
            <div>
              <label className="sc-overline block mb-2">Password (min 8)</label>
              <input required type="password" data-testid="reg-password" value={form.password} onChange={set("password")} className="sc-input-light" placeholder="••••••••" minLength={8} />
            </div>
          </div>

          <div className="pt-4 flex flex-col md:flex-row md:items-center gap-4">
            <button type="submit" disabled={loading} data-testid="reg-submit" className="sc-btn-primary">
              {loading ? "Creazione in corso..." : "Crea account e continua"}
            </button>
            <Link to="/hotelier-login" className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 hover:text-[#121212] transition-colors">
              Hai già un account? Accedi
            </Link>
          </div>

          <p className="text-[11px] text-neutral-500 leading-relaxed pt-6">
            Registrandoti accetti i nostri{" "}
            <Link to="/termini" className="underline decoration-neutral-400 hover:text-[#0B192C]">
              Termini
            </Link>{" "}
            e la{" "}
            <Link to="/privacy" className="underline decoration-neutral-400 hover:text-[#0B192C]">
              Privacy Policy
            </Link>
            . I fondi raccolti transitano su conto escrow indipendente.
          </p>
        </form>
      </section>

      <Footer />
    </div>
  );
}
